// UniversalVideoPlayer.jsx
// UniversalVideoPlayer.jsx
import React, { useRef, useState, useEffect } from "react";
import {
  Play,
  Pause,
  UndoDot,
  RedoDot,
  Maximize,
  Volume1,
  CircleGauge,
  Timer,
  MessagesSquare,
} from "lucide-react";
import PausedVideo from "./PausedVideo";

/**
 * Props:
 * - videoUrl: string (required) — direct mp4 or S3 link
 * - videoSettings: object (optional) — keeps compatibility with your earlier theme / thumbnail keys
 * - minHeight, maxHeight: numbers (optional)
 */
const isMp4OrS3 = (url = "") => {
  if (!url || typeof url !== "string") return false;
  const u = url.trim();
  // Accept explicit .mp4 or s3 host (commonly S3 endpoints). Also allow query params after .mp4
  if (/\.mp4($|\?)/i.test(u)) return true;
  if (/s3\.amazonaws\.com/i.test(u)) return true;
  // Common S3 virtual-hosted style: bucket.s3.region.amazonaws.com/path/file
  if (/\.s3[.-][a-z0-9-]+\.amazonaws\.com/i.test(u)) return true;
  return false;
};

const formatTime = (seconds = 0) => {
  const s = Math.floor(seconds || 0);
  const hrs = Math.floor(s / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const secs = s % 60;
  return `${hrs > 0 ? `${hrs}:` : ""}${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
};

const UniversalVideoPlayer = ({ videoUrl, videoSettings = {}, minHeight = 360, maxHeight = 720 }) => {
  const videoRef = useRef(null);
const  videoRefImg= useRef(null);
  const progressRef = useRef(null);
  const containerRef = useRef(null);

  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [videoTime, setVideoTime] = useState(0);
  const [progress, setProgress] = useState(0); // 0 - 100
  const [volume, setVolume] = useState(1); // 0 - 1
  const [speed, setSpeed] = useState(1);
  const [hoverTime, setHoverTime] = useState(null);
  const [hovering, setHovering] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [hoverVideo, setHoverVideo] = useState(false);
  const [speedShow, setSpeedShow] = useState(false);
  
    const [screenHeight, setScreenHeight] = useState(window.innerHeight);

      useEffect(() => {
        const handleResize = () => {
          setScreenHeight(window.innerHeight);
        };
    
        window.addEventListener("resize", handleResize);
    
        return () => {
          window.removeEventListener("resize", handleResize);
        };
      }, []);
  useEffect(() => {
    if (!isMp4OrS3(videoUrl)) {
      setError("Unsupported URL. Provide a direct .mp4 link or an S3 video URL.");
      return;
    }
    setError(null);
  }, [videoUrl]);

  // video event handlers
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    const onLoadedMetadata = () => {
      setDuration(v.duration || 0);
      setReady(true);
    };
    const onTimeUpdate = () => {
      const t = v.currentTime || 0;
      setVideoTime(t);
      if (duration > 0) setProgress((t / duration) * 100);
    };
    const onEnded = () => {
      setIsPlaying(false);
    };
    const onError = () => {
      setError("Video failed to load or play. Check CORS or the file URL.");
      setReady(false);
    };

    v.addEventListener("loadedmetadata", onLoadedMetadata);
    v.addEventListener("timeupdate", onTimeUpdate);
    v.addEventListener("ended", onEnded);
    v.addEventListener("error", onError);

    return () => {
      v.removeEventListener("loadedmetadata", onLoadedMetadata);
      v.removeEventListener("timeupdate", onTimeUpdate);
      v.removeEventListener("ended", onEnded);
      v.removeEventListener("error", onError);
    };
  }, [duration]);

  useEffect(() => {
    // apply volume and speed to video element
    const v = videoRef.current;
     

    if (!v) return;
      const videoImg = videoRefImg.current;
   if (!videoImg) return;
    v.volume = volume;
    v.playbackRate = speed;
  }, [volume, speed]);

  const handlePlayPause = () => {
    const v = videoRef.current;
    if (!v) return;
    if (isPlaying) {
      v.pause();
      setIsPlaying(false);
    } else {
      const playPromise = v.play();
      // some browsers give a promise
      if (playPromise && playPromise.then) {
        playPromise
          .then(() => setIsPlaying(true))
          .catch((e) => setError("Playback blocked (autoplay/cors) — user interaction required."));
      } else {
        setIsPlaying(true);
      }
    }
  };

  const handleSeekFromSlider = (e) => {
    const val = Number(e.target.value);
    const t = (val / 100) * duration;
    const v = videoRef.current;
    if (v) {
      v.currentTime = t;
      const videoImg = videoRefImg.current;
   if (!videoImg) return;
     videoImg.currentTime = t;
    }
    setProgress(val);
    setVideoTime(t);
  };

  const handleMouseMoveOnSlider = (e) => {
    const rect = progressRef.current?.getBoundingClientRect();
    if (!rect || !duration) return;
    const x = e.clientX - rect.left;
    const pct = Math.max(0, Math.min(1, x / rect.width));
   
    setHoverTime(pct * duration);
     const videoImg = videoRefImg.current;
   if (!videoImg) return;
     videoImg.currentTime = pct * duration;
   
    setHovering(true);
  }; 
  const handleMouseLeaveSlider = () => {
    setHoverTime(null);
    setHovering(false);
  };

  const handleSkip = (sec) => {
    const v = videoRef.current;
    if (!v) return;
    const nt = Math.max(0, Math.min(duration, v.currentTime + sec));
    v.currentTime = nt;
    setVideoTime(nt);
    if (duration > 0) setProgress((nt / duration) * 100);
  };

  const handleVolumeChange = (e) => {
    const v = Number(e.target.value) / 100;
    setVolume(v);
    if (videoRef.current) videoRef.current.volume = v;
  };

  const handleSpeedChange = (newSpeed) => {
    setSpeed(newSpeed);
    if (videoRef.current) videoRef.current.playbackRate = newSpeed;
  };

  const toggleFullScreen = async () => {
    try {
      if (!isFullScreen) {
        if (containerRef.current.requestFullscreen) await containerRef.current.requestFullscreen();
        else if (containerRef.current.webkitRequestFullscreen) containerRef.current.webkitRequestFullscreen();
        setIsFullScreen(true);
      } else {
        if (document.exitFullscreen) await document.exitFullscreen();
        setIsFullScreen(false);
      }
    } catch (err) {
      // ignore fullscreen errors
    }
  };

  // poster thumbnail: prefer videoSettings.videoThumbnail.src else none
  const poster = videoSettings?.videoThumbnail?.src || "";

  // container style
  const containerStyle = {
    width: "100%",borderRadius:"10px",
    margin: "0 auto",
    overflow: "hidden",
    position: "relative",
    minHeight: `${minHeight}px`,
    maxHeight: `${maxHeight}px`,
    background: "#000",
  };

  const gradientColor = videoSettings?.videoPlayButton?.playButtonColor?.normal?.color || "#222222";
const outerStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height:  isFullScreen ? screenHeight-60 : minHeight+30, // Full viewport height
    width: "100%", // Full viewport width
    position: "relative", // Ensure the outer div spans the full space
    backgroundColor: "transparent", // Optional background for visibility
  };
 const innerStyleTwo= {
  position:"relative",
    border:"7px solid rgba(256, 256, 256, 0.50302)", // Optional background for visibility
    color: "white",
    padding: "20px",borderRadius:`${videoSettings?.videoPlayButton?.playButtonBorderRadius?.value}px`,
    width: "50px", // Adjust as needed
    height: "50px", // Adjust as needed
   
  };
  const innerStyle = { display: "flex",
    justifyContent: "center",
    alignItems: "center",textAlign: "center",cursor:"pointer",
    padding: "20px",borderRadius:`${videoSettings?.videoPlayButton?.playButtonBorderRadius?.value}px`,
    width: "50px", // Adjust as needed
    height: "50px", // Adjust as needed
    background: `${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color?videoSettings?.videoPlayButton?.playButtonColor?.normal?.color:"#222222"}`, // Optional background for visibility
    
  };
  return (
    <div ref={containerRef} style={containerStyle}>
      {/* Error overlay */}
      {error && (
        <div
          style={{
            position: "absolute",
            zIndex: 40,
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.7)",
            color: "#fff",
            padding: 20,
            textAlign: "center",
          }}
        >
          <div>
            <div style={{ fontWeight: 800, marginBottom: 8 }}>Video Error</div>
            <div style={{ opacity: 0.9 }}>{error}</div>
          </div>
        </div>
      )}

      {/* Video element */}
      <video
        ref={videoRef}
        src={videoUrl}
        poster={poster || undefined}
        style={{ width: "100%", height: "100%", display: "block", background: "#000" }}
        preload="metadata"
        playsInline
        // controls hidden because we use custom UI
      />
 <div
          onClick={handlePlayPause}
          onMouseMove={()=>{setHoverVideo(true);}} 
        
          onMouseLeave={()=>{setHoverVideo(false)}}
          style={{
            position: "absolute",
            zIndex: 2,
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "auto",
          }}
        ></div>
      {/* Large overlay play button when not playing */}
       {!isPlaying ?<div align="center" style={{position:"absolute",background:!videoSettings?.videoThumbnail?.hideThumbnail?"#000":"",top:"0px",
              bottom:"0px",left:"0px",right:"0px",zIndex:"99999999"}}>
    {/*        
            {
      default: "https://img.youtube.com/vi/flyaWBnDt4Q/default.jpg",
      highQuality: "https://img.youtube.com/vi/flyaWBnDt4Q/hqdefault.jpg",
      mediumQuality: "https://img.youtube.com/vi/flyaWBnDt4Q/mqdefault.jpg",
      standardDefinition: "https://img.youtube.com/vi/flyaWBnDt4Q/sddefault.jpg",
      maxResolution: "https://img.youtube.com/vi/flyaWBnDt4Q/maxresdefault.jpg"
    }
            */}
              {!videoSettings?.videoThumbnail?.hideThumbnail?<img src={videoSettings?.videoThumbnail?.src} 
        style={{height: isFullScreen ? screenHeight : "",width:isFullScreen ? "" :"100%"}}/>:<div style={{height: isFullScreen ? screenHeight : "",width:isFullScreen ? "" :"100%"}}></div>}
    <div style={{position:"absolute",height:"70px",background:"transparent",
              top:"-12%",bottom:"0px",left:"0%",right:"0px"}}>
                <div style={outerStyle}>
                {duration?<div onClick={handlePlayPause}  style={innerStyle}> 
          <div className="innerStyleTwoPlayOuter" style={innerStyleTwo}> 
          <div className="innerStyleTwoPlay" ></div>
           <Play strokeWidth={3}  size={45} style={{margin:"4px"}}  color="#fff"/>
              </div>
              </div>:<div   style={innerStyle}> 
          <div  style={innerStyleTwo}> 
          <div className="innerStyleTwoPlay" ></div>
           <Play strokeWidth={3}  size={45} style={{margin:"4px"}}  color="#fff"/>
              </div>
              </div>}
              <div style={{position:"absolute",height:"79px",background:"#222",
              width:"177px",top:"70%",borderRadius:"25px",padding:"0px"}}>
       <div style={{display: "flex",
        gap: "1px", justifyContent: "center", alignItems: "center" ,padding:"10px"}}><Timer strokeWidth={3}  size={20}   color="#fff"/>
        <span style={{fontSize:"18px",fontWeight:"900",color:"#fff",paddingLeft:"15px"}}> <span style={{fontSize:"18px",color:`${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color?videoSettings?.videoPlayButton?.playButtonColor?.normal?.colors?.color10:"#222222"}`}}>{speed}</span>x </span> 
     </div>  
     <div style={{position:"absolute",height:"39px",background:"#333",
              width:"94%",borderBottomLeftRadius:"25px",borderBottomRightRadius:"25px",borderTopLeftRadius:"5px"
              ,borderTopRightRadius:"5px"
              ,padding:"0px",marginLeft:"5px",marginRight:"5px",marginBottom:"5px",bottom:"0px"}}>
     <div style={{display: "flex",
        gap: "1px", justifyContent: "center", alignItems: "center" ,padding:"10px"}}>
           <span style={{fontSize:"16px",fontWeight:"900",color:"#fff",paddingLeft:"15px"}}> 
           {duration?<span style={{fontSize:"18px",color:`${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color?videoSettings?.videoPlayButton?.playButtonColor?.normal?.colors?.color10:"#222222"}`}}> {videoTime?formatTime(videoTime)+" / ":"Duration: "} </span> :""} {duration?formatTime(duration):"Video is Not Ready."} </span> 
     </div> 
              </div>
              </div>
        </div>
        
              </div>
            
            </div>:""}

      {/* Controls bar (bottom) */}
      <div
      onMouseMove={()=>{setHoverVideo(true)}} 
        onMouseLeave={()=>{setTimeout(()=>{setHoverVideo(false)},1000)}}
        style={{position:"absolute",opacity:hoverVideo?"1":"0",zIndex:"99",bottom:"0px",left:"0px",right:"0px",
      background:"rgba(1,1,1,0.8)",height:"52px"}}
      >
        {/* progress slider with hover preview */}
        <div
          ref={progressRef}
          onMouseMove={handleMouseMoveOnSlider}
          onMouseLeave={handleMouseLeaveSlider}
          style={{
          position: "relative",
          margin: "0px",
          padding: "0 0px",
        }}  className="slider-container"    >
          <input
            type="range"
            min="0"
            max="100"
            value={progress}
             
            onChange={handleSeekFromSlider}
            style={{
              width: "100%",
              height: 6,
              appearance: "none",
              }}
           
          className="video-player-custom-slider"
          />
          {hoverTime !== null && (
            <div
              style={{
                position: "absolute",
                top: -90,
                left: `${(hoverTime / Math.max(duration, 1)) * 100}%`,
                transform: "translateX(-50%)",
                background: "#333",
                color: "#fff",
                padding: 6,
                borderRadius: 6,
                minWidth: 100,
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: 12 }}>{formatTime(hoverTime)}</div>
             <video
        ref={videoRefImg}
        src={videoUrl}
        // muted={true}
        // playsInline={true}
        // // no controls
        // controls={false}
        // // disable keyboard focus
        // tabIndex={-1}
        // don't show default context menu
        onContextMenu={(e) => e.preventDefault()}
        // prevent easy interaction (but keep pointer events on wrapper available)
        style={{ width: '120px', height: '70px', display: 'block' }}
      />
            </div>
          )}
        </div>

        {/* controls row */}
         <div onMouseEnter={() => {}} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px', paddingLeft: '10px', zIndex: 9999 }}>
        
                  <div style={{ marginTop: 0, display: 'flex', gap: 0, justifyContent: 'space-between' }}>
                    <div className="btnPlayerCircleHover" style={{ cursor: 'pointer' }} onClick={handlePlayPause}>
                      {isPlaying ? <Pause style={{ margin: 6 }} strokeWidth={2} size={22} color="#fff" /> : <Play style={{ margin: 6 }} strokeWidth={2} size={22} color="#fff" />}
                    </div>
                    <div className="btnPlayerCircleHover" style={{ cursor: 'pointer' }} onClick={() => handleSkip(-10)}>
                      <UndoDot style={{ margin: 6 }} strokeWidth={2} size={25} color="#fff" />
                    </div>
                    <div className="btnPlayerCircleHover" style={{ cursor: 'pointer' }} onClick={() => handleSkip(10)}>
                      <RedoDot style={{ margin: 6 }} strokeWidth={2} size={25} color="#fff" />
                    </div>
                  </div>
        
                  <div style={{ marginTop: 0, display: 'flex', gap: '15px', justifyContent: 'space-between' }}>
                    <div className="btnPlayerCircleHover hideMessageBtnnXs" style={{ cursor: 'pointer' }}><MessagesSquare strokeWidth={2} style={{ margin: 5 }} size={25} color="#fff" /></div>
        
                    <div style={{ display: 'flex', gap: 0, justifyContent: 'row' }} className="slider-container-volume">
                      <Volume1 strokeWidth={2} style={{ marginTop: -8 }} size={35} color="#fff" />
                      <input
                        className="video-player-custom-slider-volume"
                        type="range"
                        min="0"
                        max="100"
                        value={Math.round((volume || 0) * 100)}
                        onChange={handleVolumeChange}
                        title="Volume"
                      />
                    </div>
        
                     <div className="btnPlayerSquareHover" 
                           style={{cursor:"pointer",display: "flex",
                             gap: "1px",width:"60px" }}onClick={()=>setSpeedShow(!speedShow)}><CircleGauge strokeWidth={2}
                            style={{margin:"5px"}}  size={25} color="#fff"/>
                              <div style={{fontSize:"16px",marginTop:"6px",fontWeight:"900",color:"#fff",}}> 
                              <span style={{fontSize:"18px",color:`${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color?videoSettings?.videoPlayButton?.playButtonColor?.normal?.colors?.color10:"#222222"}`}}>{speed}</span>x</div></div>
                       
        
                     {speedShow?<div style={{position:"absolute",height:"79px",background:"#222",
                              width:"200px",borderRadius:"25px",padding:"0px",top:"-80px",right:"20px"}}>
                       <div style={{display: "flex",
                        gap: "1px", justifyContent: "center", alignItems: "center" ,padding:"10px"}}><Timer strokeWidth={3}  size={20}   color="#fff"/>
                        <span style={{fontSize:"18px",fontWeight:"900",color:"#fff",paddingLeft:"15px"}}> 
                           <span style={{fontSize:"18px",color:`${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color?videoSettings?.videoPlayButton?.playButtonColor?.normal?.colors?.color10:"#222222"}`}}>{speed}</span>x </span> 
                     </div>  
                     <div style={{position:"absolute",height:"39px",background:"#333",
                              width:"94%",borderBottomLeftRadius:"25px",borderBottomRightRadius:"25px",borderTopLeftRadius:"5px"
                              ,borderTopRightRadius:"5px"
                              ,padding:"0px",marginLeft:"5px",marginRight:"5px",marginBottom:"5px",bottom:"0px"}}>
                     <div style={{display: "flex",
                        gap: "10px", justifyContent: "center", alignItems: "center" ,padding:"10px"}}>
                         <div  style={{cursor:"pointer"}}onClick={()=>handleSpeedChange(0.85)}>
                          <span style={{fontSize:"16px",fontWeight:"900",color:"#fff",}}> 
                          <span style={{fontSize:"18px",color:`${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color?videoSettings?.videoPlayButton?.playButtonColor?.normal?.colors?.color10:"#222222"}`}}>.85</span>x</span>
                            </div>  <div  style={{cursor:"pointer"}}onClick={()=>handleSpeedChange(1)}>
                          <span style={{fontSize:"16px",fontWeight:"900",color:"#fff",}}> 
                          <span style={{fontSize:"18px",color:`${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color?videoSettings?.videoPlayButton?.playButtonColor?.normal?.colors?.color10:"#222222"}`}}>1</span>x</span>
                            </div> 
                            <div  style={{cursor:"pointer"}}onClick={()=>handleSpeedChange(1.2)}>
                          <span style={{fontSize:"16px",fontWeight:"900",color:"#fff",}}> 
                          <span style={{fontSize:"18px",color:`${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color?videoSettings?.videoPlayButton?.playButtonColor?.normal?.colors?.color10:"#222222"}`}}>1.2</span>x</span>
                            </div> <div  style={{cursor:"pointer"}} onClick={()=>handleSpeedChange(1.5)}>
                          <span style={{fontSize:"16px",fontWeight:"900",color:"#fff"}}> 
                          <span style={{fontSize:"18px",color:`${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color?videoSettings?.videoPlayButton?.playButtonColor?.normal?.colors?.color10:"#222222"}`}}>1.5</span>x</span>
                            </div> 
                            <div 
                            style={{cursor:"pointer"}}onClick={()=>handleSpeedChange(2)}>
                          <span style={{fontSize:"16px",fontWeight:"900",color:"#fff",}}> 
                          <span style={{fontSize:"18px",color:`${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color?videoSettings?.videoPlayButton?.playButtonColor?.normal?.colors?.color10:"#222222"}`}}>2</span>x</span>
                            </div> 
                     </div> 
                              </div>
                              </div>:""}
                    <div className="btnPlayerCircleHover" style={{ cursor: 'pointer' }} onClick={toggleFullScreen}><Maximize strokeWidth={2} style={{ margin: 5 }} size={25} color="#fff" /></div>
                  </div>
        
                </div>
      </div>
 <style>{
 `.slider-container {
  width: 99%; 
  margin-top: -11px !important;
  text-align: center;
}.slider-container-volume {
  width: 90px;margin-top: 10px;
  
  text-align: center;
}
  .innerStyleTwoPlayOuter:hover{background:${videoSettings?.videoPlayButton?.playButtonColor?.hover?.color?videoSettings?.videoPlayButton?.playButtonColor?.hover?.color:"#fff"} !important; }
.innerStyleTwoPlay{animation: PlayButtonAnim 2.8s infinite linear;
  border:7px solid rgba(256, 256, 256, 0.50302);
  position: absolute;top:0px;left:0px;right:0px;bottom: 0px;
  padding: 20px;border-radius:${videoSettings?.videoPlayButton?.playButtonBorderRadius?.value}px;
  width: 35px; 
  height: 35px;}
@keyframes PlayButtonAnim{
  0% {
      /* opacity: 0; */
      transform: scale(1.4)
  }
  50% {
      transform: scale(0.81)
  }
 
  100% {
      transform: scale(1.4)
  }
  }

  .btnPlayerCircleHover{border-radius:50%;width:35px;height:35px;border:2px solid transparent}
  .btnPlayerCircleHover:hover{border-radius:50%;;background:rgba(255,255,255,0.1);border:2px solid transparent}
  .btnPlayerSquareHover{border-radius:25%;border:2px solid transparent}
.btnPlayerSquareHover:hover{border-radius:25%;;background:rgba(255,255,255,0.1);border:2px solid transparent}
.video-player-custom-slider {
  -webkit-appearance: none; /* Remove default slider styles */
  appearance: none;
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, ${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color?videoSettings?.videoPlayButton?.playButtonColor?.normal?.color:"#222222"},  ${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color?videoSettings?.videoPlayButton?.playButtonColor?.normal?.color:"#222222"});;
  border-radius: 5px;
  outline: none;
  cursor: pointer;
}
  
.video-player-custom-slider-volume {
  -webkit-appearance: none; /* Remove default slider styles */
  appearance: none;
  width: 100%;
  height: 9px;
  background: linear-gradient(90deg, #f1f1f1, #fff);
  border-radius: 5px;
  outline: none;
  cursor: pointer;
}
.video-player-custom-slider:hover {
  background:  ${videoSettings?.videoPlayButton?.playButtonColor?.hover?.color?videoSettings?.videoPlayButton?.playButtonColor?.hover?.color:"#222222"}';
}

/* Custom Thumb */
.video-player-custom-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  background:  ${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color?videoSettings?.videoPlayButton?.playButtonColor?.normal?.color:"#222222"};
  border-radius: 50%;
  border: 2px solid #fff;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
}

.video-player-custom-slider::-moz-range-thumb {
  width: 10px;
  height: 10px;
  background:  ${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color};
  border-radius: 50%;
  border: 2px solid #fff;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
}

/* Hover and Active States */
.video-player-custom-slider::-webkit-slider-thumb:hover {
  background:  ${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color};
}

.video-player-custom-slider::-webkit-slider-thumb:active {
  background:  ${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color};
}




.video-player-custom-slider-volume::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 20px;
  height: 20px;
  background:  ${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color};
  border-radius: 50%;
  border: 2px solid #fff;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
}

.video-player-custom-slider-volume::-moz-range-thumb {
  width: 20px;
  height: 20px;
  background:  ${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color};
  border-radius: 50%;
  border: 2px solid #fff;
  cursor: pointer;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
}

/* Hover and Active States */
.video-player-custom-slider-volume::-webkit-slider-thumb:hover {
  background:  ${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color};
}

.video-player-custom-slider-volume::-webkit-slider-thumb:active {
  background:  ${videoSettings?.videoPlayButton?.playButtonColor?.normal?.color};
}


 /* lg */ 
 @media (min-width: 1200px) {

 }
 /* md */
 @media (min-width: 992px) and (max-width: 1199px) {
 }
  /* sm */
@media (min-width: 768px) and (max-width: 991px) {

}
 /* xs */
@media (max-width: 767px) {
  .hideMessageBtnnXs{display: none !important;}
   } `}</style>
    </div>
  );
};

export default UniversalVideoPlayer;
